import { REPORT_ANALYSIS_MAX_BARS } from "./candleTimeframeBarBudget";

export const MARKET_CONTEXT_BAR_COVERAGE_CAP_LABEL =
  REPORT_ANALYSIS_MAX_BARS.toLocaleString();

export type MarketContextCoverageSession =
  | "crypto"
  | "fxMetals"
  | "usFutures"
  | "usEquitiesExtended"
  | "usEquitiesRth";

export type MarketContextCoverageRow = {
  timeframe: string;
  /** True for 4h and 1d — displayed with rounded long-horizon labels. */
  longHorizon: boolean;
  crypto: string;
  fxMetals: string;
  usFutures: string;
  usEquitiesExtended: string;
  usEquitiesRth: string;
};

/** Hardcoded coverage at 60,000 bars (trading time × session hours). */
export const MARKET_CONTEXT_BAR_COVERAGE_ROWS: MarketContextCoverageRow[] = [
  {
    timeframe: "1m",
    longHorizon: false,
    crypto: "~6 weeks",
    fxMetals: "~2 months",
    usFutures: "~2 months",
    usEquitiesExtended: "~3 months",
    usEquitiesRth: "~7 months",
  },
  {
    timeframe: "5m",
    longHorizon: false,
    crypto: "~7 months",
    fxMetals: "~10 months",
    usFutures: "~10 months",
    usEquitiesExtended: "~1.2 years",
    usEquitiesRth: "~2.9 years",
  },
  {
    timeframe: "15m",
    longHorizon: false,
    crypto: "~1.7 years",
    fxMetals: "~2.5 years",
    usFutures: "~2.5 years",
    usEquitiesExtended: "~3.6 years",
    usEquitiesRth: "~8.8 years",
  },
  {
    timeframe: "30m",
    longHorizon: false,
    crypto: "~3.4 years",
    fxMetals: "~5 years",
    usFutures: "~5 years",
    usEquitiesExtended: "~7.2 years",
    usEquitiesRth: "~17.7 years",
  },
  {
    timeframe: "1h",
    longHorizon: false,
    crypto: "~6.8 years",
    fxMetals: "~10 years",
    usFutures: "~10 years",
    usEquitiesExtended: "~14 years",
    usEquitiesRth: "~35 years",
  },
  {
    timeframe: "4h",
    longHorizon: true,
    crypto: "~27 years",
    fxMetals: "~40 years",
    usFutures: "~40 years",
    usEquitiesExtended: "~58 years",
    usEquitiesRth: "~142 years",
  },
  {
    timeframe: "1d",
    longHorizon: true,
    crypto: "~164 years",
    fxMetals: "~240 years",
    usFutures: "~240 years",
    usEquitiesExtended: "~345 years",
    usEquitiesRth: "~849 years",
  },
];

export const MARKET_CONTEXT_COVERAGE_SESSION_COLUMNS: {
  key: MarketContextCoverageSession;
  label: string;
  shortLabel: string;
}[] = [
  { key: "crypto", label: "Crypto (24/7)", shortLabel: "Crypto" },
  { key: "fxMetals", label: "FX & metals (24/5)", shortLabel: "FX & metals" },
  { key: "usFutures", label: "US futures (Globex)", shortLabel: "US futures" },
  {
    key: "usEquitiesExtended",
    label: "US equities (extended)",
    shortLabel: "US equities (ext.)",
  },
  { key: "usEquitiesRth", label: "US equities (RTH)", shortLabel: "US equities (RTH)" },
];

/**
 * Round very long 4h/1d spans so the table stays readable — these are theoretical
 * maxima at the bar cap, not typical report windows.
 */
export function formatMarketContextCoverageCell(
  raw: string,
  longHorizon: boolean
): string {
  if (!longHorizon) return raw;

  const yearsMatch = raw.match(/~?\s*([\d.]+)\s*years?/i);
  if (yearsMatch) {
    const years = Number.parseFloat(yearsMatch[1]);
    if (Number.isFinite(years)) {
      if (years >= 100) return "Centuries+";
      if (years >= 25) return "25+ years";
      if (years >= 10) return "10+ years";
    }
  }

  return raw;
}

export function marketContextCoverageCell(
  row: MarketContextCoverageRow,
  session: MarketContextCoverageSession
): string {
  return formatMarketContextCoverageCell(row[session], row.longHorizon);
}
